import { createRoot } from 'react-dom/client'
import App from './views/index'
import { Auth0Provider } from '@equipmentshare/auth0-react'
import config from './config/config'
import { QueryClient, QueryClientProvider } from 'react-query'
import { EuiProvider, THEME_TOKENS } from '@equipmentshare/ds2'
import { FlagProvider } from '@equipmentshare/feature-flags'
import { initializeAnalytics } from './services/analytics'
import { initializeIntercomSupport } from './services/intercom'
import { initDatadog } from './datadog/init'
import { LicenseInfo } from '@mui/x-license'
import { ThemeProvider } from '@mui/material' // eslint-disable-line no-restricted-imports
import muiTheme from './config/muiTheme'

// Import lazy EUI icons to fix Vite dynamic import issues
import '@equipmentshare/ds2-eui-icons/lazy'

import './index.css'

// Initialize Datadog RUM before render so early crashes are still captured
try {
	initDatadog()
} catch ( error ) {
	console.error( 'Failed to initialize Datadog RUM', error )
}

// Configure MUI X Premium license
if ( import.meta.env.VITE_MUI_X_LICENSE_KEY ) {
	LicenseInfo.setLicenseKey( import.meta.env.VITE_MUI_X_LICENSE_KEY )
}

// Initialize Heap Analytics
initializeAnalytics()

// Initialize Intercom customer support
initializeIntercomSupport()

const rootEl = document.getElementById( 'root' )
const queryClient = new QueryClient()

// In local dev (`yarn start`), persist the Auth0 token cache in localStorage so a
// refresh token survives page reloads and the SDK can silently re-auth without a
// trip to /authorize. Deployed builds keep the cache in memory — localhost redirect
// URIs can never skip the Auth0 consent screen, so memory caching there would prompt
// for consent on every refresh. import.meta.env.DEV is true only for the dev server.
const authCacheLocation = import.meta.env.DEV ? 'localstorage' : 'memory'

if ( !rootEl ) {
	throw new Error( 'Failed to find the root element' )
}

// Register MUI X License
if ( import.meta.env.VITE_MUI_LICENSE ) {
	LicenseInfo.setLicenseKey( import.meta.env.VITE_MUI_LICENSE )
}

const root = createRoot( rootEl )
root.render(
	<EuiProvider modify={THEME_TOKENS}>
		<ThemeProvider theme={muiTheme}>
			<QueryClientProvider client={queryClient}>
				<Auth0Provider
					domain={config.AUTH0_DOMAIN}
					clientId={config.AUTH0_CLIENTID}
					redirectUri={window.location.origin}
					audience={config.AUTH0_AUDIENCE}
					cacheLocation={authCacheLocation}
					useCookiesForTransactions
					useRefreshTokens
				>
					<FlagProvider clientId={config.LAUNCH_DARKLY_CLIENT_ID}>
						<App />
					</FlagProvider>
				</Auth0Provider>
			</QueryClientProvider>
		</ThemeProvider>
	</EuiProvider>
)
